import { useId } from "react";
import {
  AlertTriangle,
  ArrowRight,
  BarChart3,
  Database,
  Eye,
  GraduationCap,
  Loader2,
  ShieldCheck,
  Smartphone,
  X,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDialogBehaviour } from "./useDialogBehaviour";

export interface ConsentModalProps {
  open: boolean;
  busy?: boolean;
  error?: string;
  onAccept: () => void;
  onDecline: () => void;
}

// What a participant is agreeing to, in the order they would ask about it.
// Each entry is a heading and a short body in the locale files; keep the
// wording to what the app actually does.
const sections: { icon: LucideIcon; key: string }[] = [
  { icon: GraduationCap, key: "consentAbout" },
  { icon: Database, key: "consentCollect" },
  { icon: BarChart3, key: "consentUse" },
  { icon: Eye, key: "consentAccess" },
  { icon: Smartphone, key: "consentDevice" },
  { icon: ShieldCheck, key: "consentChoices" },
];

// Shown once, over the login screen, the first time a teacher or facilitator
// signs in. Closing it is the same as declining, which discards the sign-in.
export function ConsentModal({
  open,
  busy = false,
  error,
  onAccept,
  onDecline,
}: ConsentModalProps) {
  const { t } = useTranslation();
  const id = useId();
  const panel = useDialogBehaviour({ open, onDismiss: onDecline, dismissible: !busy });

  if (!open) return null;

  return (
    <div className="fixed inset-y-0 left-1/2 z-50 flex w-full max-w-md -translate-x-1/2 items-center justify-center overflow-y-auto bg-app-ink/40 p-3 backdrop-blur-[3px]">
      <div
        ref={panel}
        className="my-auto flex w-full max-w-sm flex-col gap-4 rounded-3xl border border-app-line bg-surface p-5 text-start shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        id={id}
        tabIndex={-1}
      >
        <div className="flex items-start gap-3">
          <span className="grid size-10 shrink-0 place-items-center rounded-full bg-accent-soft text-accent">
            <ShieldCheck size={20} aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 id={`${id}-title`} className="text-base font-bold leading-snug text-app-ink">
              {t("consentTitle")}
            </h2>
            <p className="mt-0.5 text-xs leading-relaxed text-app-muted">{t("consentIntro")}</p>
          </div>
          <button
            type="button"
            className="shrink-0 rounded-full p-1 text-app-icon transition-colors hover:bg-app-field hover:text-app-ink disabled:opacity-60"
            aria-label={t("consentDecline")}
            onClick={onDecline}
            disabled={busy}
          >
            <X size={20} />
          </button>
        </div>

        <div className="max-h-[52dvh] overflow-y-auto pe-1" tabIndex={0}>
          <ul className="flex flex-col gap-4">
            {sections.map(({ icon: Icon, key }) => (
              <li key={key} className="flex gap-3">
                <span className="mt-0.5 grid size-8 shrink-0 place-items-center rounded-lg bg-app-field text-accent">
                  <Icon size={16} aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <h3 className="text-sm font-semibold text-app-ink">{t(`${key}Title`)}</h3>
                  <p className="mt-0.5 text-xs leading-relaxed text-app-copy">{t(`${key}Body`)}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {error && (
          <p className="flex items-center gap-1.5 rounded-md bg-danger-bg px-3 py-2 text-xs text-accent-strong">
            <AlertTriangle size={14} className="shrink-0" />
            {error}
          </p>
        )}

        <div className="flex flex-col gap-2 border-t border-app-line pt-3">
          <p className="text-[11px] leading-relaxed text-app-muted">{t("consentAgree")}</p>
          <button
            type="button"
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-accent px-4 py-3 text-xs font-semibold text-white shadow-sm transition active:scale-[0.98] enabled:hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-60"
            onClick={onAccept}
            disabled={busy}
          >
            {busy && <Loader2 size={14} className="animate-spin" />}
            {t("consentContinue")}
            {!busy && <ArrowRight size={16} className="rtl:rotate-180" />}
          </button>
          <button
            type="button"
            className="w-full rounded-xl px-4 py-2.5 text-xs font-semibold text-app-muted transition-colors enabled:hover:bg-app-field enabled:hover:text-app-ink disabled:opacity-60"
            onClick={onDecline}
            disabled={busy}
          >
            {t("consentDecline")}
          </button>
        </div>
      </div>
    </div>
  );
}
